import { saveEncryptedApiKey as saveKey, getDecryptedApiKey, deleteApiKey, isApiKeyStored } from './encryption';

// Provider-specific API key management
type ApiProvider = 'gemini' | 'pixabay';

const PROVIDER_KEYS: Record<ApiProvider, string> = {
  gemini: 'geminiApiKey',
  pixabay: 'pixabayApiKey'
};

// Load encrypted API key for a specific provider
export const loadEncryptedApiKey = async (provider: ApiProvider): Promise<string> => {
  const storageKey = PROVIDER_KEYS[provider];
  const encrypted = localStorage.getItem(storageKey);
  
  if (!encrypted) {
    return '';
  }
  
  // Use the existing decryption function from encryption.ts
  // For provider-specific keys, we need to temporarily set the key in the expected location
  const originalKey = localStorage.getItem('geminiApiKey');
  try {
    localStorage.setItem('geminiApiKey', encrypted);
    return getDecryptedApiKey();
  } finally {
    // Restore original key
    if (originalKey) {
      localStorage.setItem('geminiApiKey', originalKey);
    } else {
      localStorage.removeItem('geminiApiKey');
    }
  }
};

// Save encrypted API key for a specific provider
export const saveEncryptedApiKey = async (apiKey: string, provider: ApiProvider): Promise<void> => {
  const storageKey = PROVIDER_KEYS[provider];
  
  // Use the existing encryption function from encryption.ts
  // For provider-specific keys, we need to temporarily work with the expected location
  const originalKey = localStorage.getItem('geminiApiKey');
  const originalTime = localStorage.getItem('geminiApiKey_savedAt');
  
  try {
    // Save using the existing encryption function
    saveKey(apiKey);
    
    // Move the encrypted data to provider-specific location
    const encrypted = localStorage.getItem('geminiApiKey');
    if (encrypted) {
      localStorage.setItem(storageKey, encrypted);
      localStorage.setItem(`${storageKey}_savedAt`, Date.now().toString());
    }
  } finally {
    // Restore original key or clean up
    if (originalKey) {
      localStorage.setItem('geminiApiKey', originalKey);
      if (originalTime) {
        localStorage.setItem('geminiApiKey_savedAt', originalTime);
      }
    } else {
      localStorage.removeItem('geminiApiKey');
      localStorage.removeItem('geminiApiKey_savedAt');
    }
  }
};

// Delete API key for a specific provider
export const deleteEncryptedApiKey = async (provider: ApiProvider): Promise<void> => {
  const storageKey = PROVIDER_KEYS[provider];
  localStorage.removeItem(storageKey);
  localStorage.removeItem(`${storageKey}_savedAt`);
};

// Check if API key is stored for a specific provider
export const isApiKeyStoredForProvider = (provider: ApiProvider): boolean => {
  const storageKey = PROVIDER_KEYS[provider];
  return !!localStorage.getItem(storageKey);
};

// Get API key saved time for a specific provider
export const getApiKeySavedTimeForProvider = (provider: ApiProvider): Date | null => {
  const storageKey = PROVIDER_KEYS[provider];
  const savedAt = localStorage.getItem(`${storageKey}_savedAt`);
  if (!savedAt) {return null;}
  
  return new Date(parseInt(savedAt));
};

// Export the original functions for backward compatibility
export { deleteApiKey, isApiKeyStored };